import { unwrapResult } from '@reduxjs/toolkit';
import { Input as AntdInput, Select, message } from 'antd';
import Loading from 'components/Loading';
import COUNTRIES from 'config/countries.json';
import { INIT_PAGINATION } from 'constants/common.constant';
import { getFlag, getLevel, getUniqueKey } from 'helpers/common.helper';
import { IQueryParam } from 'interfaces/common.interface';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import { broadcastNotification, getUser } from 'store/global/action';
import { useAppDispatch } from 'store/hooks';

const { Option } = Select;

const Notification = () => {
  const dispatch = useAppDispatch();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<IQueryParam>({
    page: INIT_PAGINATION.PAGE,
    limit: INIT_PAGINATION.LIMIT,
  });
  const [pagination, setPagination] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedPlatform, setSelectedPlatform] = useState('ios');
  const [type, setType] = useState('normal');
  const [content, setContent] = useState('');
  const [notificationLoading, setNotificationLoading] = useState(false);

  const allSelected = selectedIds.length === data.length;

  const fetchData = async () => {
    try {
      setLoading(true);

      const res: any = await unwrapResult(await dispatch(getUser(params)));

      if (res && res.items && res.pagination) {
        setData([...data, ...res.items]);
        setPagination(res.pagination);
      } else {
        setData([...[]]);
        setPagination(null);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    if (selectedIds.length === 0) {
      message.error('Please select at least one user');
      return;
    }

    if (!content.trim()) {
      message.error('Please enter some content');
      return;
    }

    try {
      setNotificationLoading(true);
      await unwrapResult(
        await dispatch(
          broadcastNotification({
            ids: selectedIds,
            content,
            platform: selectedPlatform,
            type,
          }),
        ),
      );
      setContent('');
      setSelectedIds([]);
      message.success('Sent notifications successfully');
    } catch (e) {
      console.log(e);
    } finally {
      setNotificationLoading(false);
    }
  };

  const fetchMoreData = () => {
    if (params.page && params.page < pagination.totalPages) {
      setParams({ ...params, page: params.page + 1 });
    }
  };

  const resetData = () => {
    setData([...[]]);
    setPagination(null);
    setSelectedIds([]);
  };

  const searching = (e: any) => {
    resetData();

    if (e.target.value.trim() !== '') {
      setParams({
        ...params,
        keyword: e.target.value,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    } else {
      setParams({
        ...params,
        keyword: null,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    }
  };

  const selectLevel = (value: any) => {
    resetData();

    if (value !== '') {
      setParams({
        ...params,
        level: value,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    } else {
      setParams({
        ...params,
        level: null,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    }
  };

  const selectCountry = (value: any) => {
    resetData();

    if (value !== '') {
      setParams({
        ...params,
        country: value,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    } else {
      setParams({
        ...params,
        country: null,
        page: INIT_PAGINATION.PAGE,
        limit: INIT_PAGINATION.LIMIT,
      });
    }
  };

  const toggleCheckbox = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const isChecked = (id: string) => selectedIds.includes(id);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data.map((user) => user._id));
    }
  };

  const handleChangePlatform = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPlatform(e.target.value);
  };

  const handleChangeType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setType(e.target.value);
  };

  useEffect(() => {
    if (params) {
      fetchData();
    }
  }, [params]);

  useEffect(() => {
    if (pagination) {
      if (pagination.currentPage < pagination.totalPages) {
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    }
  }, [pagination]);

  return (
    <>
      <div className="content notification-content">
        <Row>
          <Col md="12">
            <Button
              color="primary"
              disabled={notificationLoading}
              onClick={send}
            >
              Send
            </Button>
          </Col>
        </Row>

        <Row>
          <Col md="12">
            <Form>
              <FormGroup>
                <Label for="type">Type</Label>
                <FormGroup check inline>
                  <Label check>
                    <Input
                      type="radio"
                      name="normal"
                      value="normal"
                      checked={type === 'normal'}
                      onChange={handleChangeType}
                    />
                    Normal
                  </Label>
                </FormGroup>
                <FormGroup check inline>
                  <Label check>
                    <Input
                      type="radio"
                      name="update"
                      value="update"
                      checked={type === 'update'}
                      onChange={handleChangeType}
                    />
                    Force Update
                  </Label>
                </FormGroup>
              </FormGroup>

              <FormGroup>
                <Label for="platform">Platform</Label>
                <FormGroup check inline>
                  <Label check>
                    <Input
                      type="radio"
                      name="ios"
                      value="ios"
                      checked={selectedPlatform === 'ios'}
                      onChange={handleChangePlatform}
                    />
                    iOS
                  </Label>
                </FormGroup>
                <FormGroup check inline>
                  <Label check>
                    <Input
                      type="radio"
                      name="android"
                      value="android"
                      checked={selectedPlatform === 'android'}
                      onChange={handleChangePlatform}
                    />
                    Android
                  </Label>
                </FormGroup>
              </FormGroup>

              <FormGroup>
                <Label for="content">Content</Label>
                <Input
                  type="textarea"
                  name="content"
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter your content here..."
                />
              </FormGroup>
            </Form>
          </Col>
        </Row>

        <Row>
          <div className="multiple-fields">
            <div className="multiple-fields__item">
              <AntdInput
                className="default"
                placeholder="Search..."
                allowClear
                onChange={debounce(searching, 1000)}
              />
            </div>

            <div className="multiple-fields__item">
              <Select
                placeholder="Select level"
                className="default"
                allowClear
                onChange={selectLevel}
              >
                {Array.from(Array(100).keys()).map((item, index) => (
                  <Option key={getUniqueKey()} value={item}>
                    Level {item}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="multiple-fields__item">
              <Select
                placeholder="Select country"
                className="default"
                allowClear
                onChange={selectCountry}
              >
                {COUNTRIES.data.map((item, index) => (
                  <Option key={getUniqueKey()} value={item.code}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        </Row>

        <Row>
          <Col md="12">
            {data?.length > 0 ? (
              <Card>
                <CardBody>
                  <InfiniteScroll
                    dataLength={data.length}
                    next={fetchMoreData}
                    hasMore={hasMore}
                    loader={null}
                    endMessage={
                      <p className="all-items-txt">You have seen all items</p>
                    }
                  >
                    <Table responsive>
                      <thead className="text-primary">
                        <tr>
                          <th></th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Country</th>
                          <th>Level</th>
                          <th>Created At</th>
                          <th>
                            <Input
                              type="checkbox"
                              checked={allSelected}
                              onChange={toggleSelectAll}
                            />
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {data.map((item, index) => (
                          <tr key={getUniqueKey()}>
                            <td className="avatar">
                              <img
                                alt="photo"
                                src={
                                  item.thumbnail
                                    ? item.thumbnail.small
                                    : require('assets/img/avatar.png')
                                }
                              />
                            </td>
                            <td className="link">
                              <a href={`user/${item._id}`}>{item.fullName}</a>
                            </td>
                            <td>{item.email}</td>
                            <td className="flag">
                              {item.country ? getFlag(item.country) : ''}
                            </td>
                            <td>{getLevel(item.beamTimes)}</td>
                            <td>
                              {moment(item.createdAt).format('YYYY-MM-DD')}
                            </td>
                            <td>
                              <Input
                                type="checkbox"
                                checked={isChecked(item._id)}
                                onChange={() => toggleCheckbox(item._id)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </InfiniteScroll>
                </CardBody>
              </Card>
            ) : (
              <p className="all-items-txt">No items.</p>
            )}

            {loading && <Loading />}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default Notification;
